import type {
  FactsetChartAxisColor,
  FactsetChartFontColor,
  FactsetChartFormat,
  FactsetStyleset,
} from "./chartCreatorTypes";

/**
 * ChartCreator's Excel export applies chartFormat but ignores the styleset, so
 * a styleset reaches a workbook only through its own definition, read from a
 * preview of the same chart: colors the export takes as chartFormat, and a
 * look the workbook's chart XML is patched with.
 *
 * In preview XML the styleset lives in the chart-level attributes (palette and
 * canvas), the FC_PLOT attributes (fonts, axes, backgrounds, grids) and the
 * FC_SERIES attributes (line width). Main Plot's attributes hold the request's
 * chartFormat, which FactSet lets override the styleset, so they are not read.
 */
const ATTRIBUTE = /<Attr>\s*(\w+)\s*=\s*([^<]*?)\s*<\/Attr>/g;
const SERIES_COLOR = /^SeriesColor(\d{1,2})$/;
const EMU_PER_PIXEL = 9525;

/**
 * FactSet's base look, which a styleset's attributes override. FactSet Basic,
 * an empty styleset, previews with exactly this: white chart and plot, #CCCCCC
 * gridlines, a black title and a #3366CC first series.
 */
const BASE_ATTRIBUTES: ReadonlyArray<[string, string]> = [
  ["SeriesColor0", "13395507"],
  ["Heading1FontColor", "0"],
  ["CanvasBGColor", "16777215"],
  ["ChartBGColor", "16777215"],
  ["PlotBGColor", "16777215"],
  ["GridFGColor", "13421772"],
];

const FONT_COLORS: Record<string, keyof FactsetChartFontColor> = {
  Heading1FontColor: "heading1FontColor",
  Heading2FontColor: "heading2FontColor",
  LegendFontColor: "legendFontColor",
};

const AXIS_COLORS: Record<string, keyof FactsetChartAxisColor> = {
  XAxisFGColor: "xAxisFGColor",
  YAxisFGColor: "yAxisFGColor",
  XFontColor: "xFontColor",
  YFontColor: "yFontColor",
  XTitleFontColor: "xTitleFontColor",
  YTitleFontColor: "yTitleFontColor",
};

/** A solid RRGGBB color, or a gradient whose angle is in degrees (90 runs top to bottom). */
export type StylesetFill = { color: string } | { from: string; to: string; angle: number };

export interface StylesetLook {
  chartFill: string;
  /** Absent when the plot is hollow, so the chart fill shows through. */
  plotFill?: StylesetFill;
  xGridColor: string;
  yGridColor: string;
  seriesLineWidthEmu?: number;
  /** Text color for cells pictured over the chart, set only when the chart is dark. */
  overlayTextColor?: string;
}

export interface StylesetPreview {
  chartFormat: FactsetChartFormat;
  look: StylesetLook;
}

function styleAttributes(fdsChartXml: string): Map<string, string> {
  const chartLevel = fdsChartXml.split(/<FDS(?:Data|Series|Plot)\b/, 1)[0];
  const appAttributes = (element: string) =>
    new RegExp(`<${element}>[\\s\\S]*?<FDSAppAttributes>([\\s\\S]*?)</FDSAppAttributes>`).exec(fdsChartXml)?.[1] ?? "";
  const attributes = new Map(BASE_ATTRIBUTES);
  for (const block of [chartLevel, appAttributes('FDSPlot ID="FC_PLOT"'), appAttributes('FDSSeries ID="FC_SERIES"')]) {
    for (const [, name, value] of block.matchAll(ATTRIBUTE)) attributes.set(name, value);
  }
  return attributes;
}

/** FactSet stores colors as Windows COLORREF integers (0x00BBGGRR). */
function colorRef(value: string | undefined): [number, number, number] | undefined {
  if (!value || !/^\d+$/.test(value)) return undefined;
  const n = Number(value);
  if (n > 0xffffff) return undefined;
  return [n & 255, (n >> 8) & 255, (n >> 16) & 255];
}

function hexColor(value: string | undefined): string | undefined {
  return colorRef(value)
    ?.map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
}

function isDark(hex: string): boolean {
  const [red, green, blue] = [0, 2, 4].map((offset) => parseInt(hex.slice(offset, offset + 2), 16));
  return 0.299 * red + 0.587 * green + 0.114 * blue < 128;
}

function chartFormatFrom(attributes: Map<string, string>): FactsetChartFormat {
  const seriesColor: Record<string, string> = {};
  const fontColor: FactsetChartFontColor = {};
  const axisColor: FactsetChartAxisColor = {};
  for (const [name, value] of attributes) {
    const rgb = colorRef(value);
    if (!rgb) continue;
    const css = `RGB(${rgb.join(",")})`;
    const series = SERIES_COLOR.exec(name);
    if (series && Number(series[1]) <= 15) seriesColor[`seriesColor${Number(series[1])}`] = css;
    else if (FONT_COLORS[name]) fontColor[FONT_COLORS[name]] = css;
    else if (AXIS_COLORS[name]) axisColor[AXIS_COLORS[name]] = css;
  }
  return {
    ...(Object.keys(seriesColor).length > 0 ? { seriesColor } : {}),
    ...(Object.keys(fontColor).length > 0 ? { fontColor } : {}),
    ...(Object.keys(axisColor).length > 0 ? { axisColor } : {}),
  };
}

/** A BGStyle of 1 is hollow; 0 or none is solid, shaded when its gradient style is 1. */
function lookFrom(attributes: Map<string, string>): StylesetLook {
  const color = (name: string) => hexColor(attributes.get(name));
  const canvas = color("CanvasBGColor") ?? "FFFFFF";
  const chartFill = attributes.get("ChartBGStyle") === "1" ? canvas : color("ChartBGColor") ?? canvas;
  const plotColor = color("PlotBGColor") ?? "FFFFFF";
  const gradientTo = color("PlotBGGradientColor");
  const angle = Number(attributes.get("PlotBGGradientAngle") ?? 90);
  const plotFill: StylesetFill | undefined =
    attributes.get("PlotBGStyle") === "1"
      ? undefined
      : attributes.get("PlotBGGradientStyle") === "1" && gradientTo
        ? { from: plotColor, to: gradientTo, angle: Number.isFinite(angle) ? angle : 90 }
        : { color: plotColor };
  const grid = color("GridFGColor") ?? "CCCCCC";
  const lineWidth = Number(attributes.get("SeriesFGWidth"));
  const dark = isDark(chartFill);
  return {
    chartFill,
    ...(plotFill ? { plotFill } : {}),
    xGridColor: color("XGridFGColor") ?? grid,
    yGridColor: color("YGridFGColor") ?? grid,
    // FactSet draws line widths in pixels at 96 dpi.
    ...(lineWidth > 0 ? { seriesLineWidthEmu: Math.round(lineWidth * EMU_PER_PIXEL) } : {}),
    ...(dark ? { overlayTextColor: color("LegendFontColor") ?? color("XFontColor") ?? "FFFFFF" } : {}),
  };
}

export function readStylesetPreview(fdsChartXml: string): StylesetPreview | undefined {
  if (!/<FDSChart\b/.test(fdsChartXml)) return undefined;
  const attributes = styleAttributes(fdsChartXml);
  return { chartFormat: chartFormatFrom(attributes), look: lookFrom(attributes) };
}

/** Explicit chartFormat values win over the styleset's. */
export function mergeChartFormat(
  styleset: FactsetChartFormat,
  explicit: FactsetChartFormat | undefined,
): FactsetChartFormat {
  if (!explicit) return styleset;
  const merged: FactsetChartFormat = { ...styleset, ...explicit };
  if (styleset.seriesColor && explicit.seriesColor) merged.seriesColor = { ...styleset.seriesColor, ...explicit.seriesColor };
  if (styleset.fontColor && explicit.fontColor) merged.fontColor = { ...styleset.fontColor, ...explicit.fontColor };
  if (styleset.axisColor && explicit.axisColor) merged.axisColor = { ...styleset.axisColor, ...explicit.axisColor };
  return merged;
}

/** Matches an id, a display name, or a display name without its "FactSet" prefix. */
export function findStyleset(
  stylesets: readonly FactsetStyleset[],
  requested: string,
): FactsetStyleset | undefined {
  const key = (value: string) => value.trim().toLowerCase().replace(/^factset\s+/, "");
  const wanted = key(requested);
  return (
    stylesets.find((styleset) => styleset.id.toLowerCase() === requested.trim().toLowerCase()) ??
    stylesets.find((styleset) => key(styleset.name) === wanted)
  );
}
